import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", password: "", role: "user", station_access: [], send_invite: true };

const ONLINE_MS = 5 * 60 * 1000;

function isOnline(lastSeen, nowMs) {
  if (!lastSeen) return false;
  const ts = Date.parse(lastSeen);
  return !isNaN(ts) && nowMs - ts < ONLINE_MS;
}

function relLastSeen(lastSeen, lang, nowMs) {
  if (!lastSeen) return null;
  const ts = Date.parse(lastSeen);
  if (isNaN(ts)) return null;
  const s = Math.round((ts - nowMs) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang || "es", { numeric: "auto" });
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, "second");
  const m = Math.round(s / 60);
  if (Math.abs(m) < 60) return rtf.format(m, "minute");
  const h = Math.round(s / 3600);
  if (Math.abs(h) < 24) return rtf.format(h, "hour");
  return rtf.format(Math.round(s / 86400), "day");
}

export default function Users() {
  const { user: me } = useAuth();
  const { t, lang } = useI18n();
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/users"), api.get("/stations")]);
    setUsers(u.data);
    setStations(s.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 20000);
    const reload = setInterval(load, 30000);
    return () => { clearInterval(tick); clearInterval(reload); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canManage = (u) => me.role === "admin" || u.role !== "admin";
  const restrictsStations = form.role === "user" || form.role === "avanzado";
  const toggleStation = (code) => {
    setForm((f) => ({
      ...f,
      station_access: f.station_access.includes(code)
        ? f.station_access.filter((c) => c !== code)
        : [...f.station_access, code],
    }));
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, station_access: u.station_access || [], send_invite: false });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name, role: form.role,
        station_access: form.role === "admin" ? [] : form.station_access,
      };
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          ...payload,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success(t("users.updated"));
      } else {
        const createBody = { ...payload, email: form.email, send_invite: form.send_invite };
        if (!form.send_invite) createBody.password = form.password;
        await api.post("/users", createBody);
        toast.success(form.send_invite ? t("users.inviteSent") : t("users.created"));
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("users.deleteConfirm"))) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success(t("users.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        actions={
          <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={openCreate} data-testid="add-user-btn">
            <Plus className="mr-2 h-4 w-4" /> {t("users.newUser")}
          </Button>
        }
      />
      <div className="p-6">
        <Card className="border-slate-200 shadow-none">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead>{t("users.connection")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-slate-900">{u.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                      u.role === "admin" ? "border-blue-200 bg-blue-50 text-blue-700"
                        : u.role === "avanzado" ? "border-violet-200 bg-violet-50 text-violet-700"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`} data-testid={`user-role-${u.email}`}>
                      {u.role === "admin" ? t("users.roleAdmin") : u.role === "avanzado" ? t("users.roleAdvanced") : t("users.roleUser")}
                    </span>
                  </TableCell>
                  <TableCell data-testid={`user-conn-${u.email}`}>
                    {isOnline(u.last_seen, now) ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        {t("users.online")}
                      </span>
                    ) : (
                      <span className="inline-flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                          <span className="h-2 w-2 rounded-full bg-slate-300" />
                          {t("users.offline")}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {u.last_seen ? relLastSeen(u.last_seen, lang, now) : t("users.never")}
                        </span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage(u) && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} className="text-slate-400 hover:text-blue-600">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {u.id !== me.id && canManage(u) && (
                      <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`delete-user-${u.email}`} className="text-slate-400 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t("users.editUser") : t("users.newUser")}</DialogTitle>
            <DialogDescription>{t("users.desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("common.email")}</Label>
              <Input type="email" data-testid="user-email-input" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
            {editing ? (
              <div>
                <Label>{t("users.newPasswordOptional")}</Label>
                <Input type="password" data-testid="user-password-input" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="••••••••" />
              </div>
            ) : (
              <>
                <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 p-3" data-testid="invite-toggle">
                  <input type="checkbox" checked={form.send_invite}
                    onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600" />
                  <span className="text-sm text-slate-700">{t("users.inviteEmail")}</span>
                </label>
                {!form.send_invite && (
                  <div>
                    <Label>{t("common.password")}</Label>
                    <Input type="password" data-testid="user-password-input" value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="••••••••" />
                  </div>
                )}
              </>
            )}
            <div>
              <Label>{t("common.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t("users.roleUser")}</SelectItem>
                  <SelectItem value="avanzado">{t("users.roleAdvanced")}</SelectItem>
                  {me.role === "admin" && <SelectItem value="admin">{t("users.roleAdmin")}</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {restrictsStations && (
              <div data-testid="station-access-block">
                <Label>{t("users.stationAccess")}</Label>
                <p className="mt-0.5 text-xs text-slate-400">{t("users.stationAccessHint")}</p>
                <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 p-2">
                  {stations.length === 0 && <p className="px-1 py-2 text-xs text-slate-400">{t("users.noStations")}</p>}
                  {stations.map((s) => (
                    <label key={s.code} data-testid={`station-access-${s.code}`}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-slate-50">
                      <input type="checkbox" checked={form.station_access.includes(s.code)}
                        onChange={() => toggleStation(s.code)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                      <span className="font-medium text-slate-700">{s.name}</span>
                      <span className="font-mono text-xs text-slate-400">{s.code}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={save} data-testid="save-user-btn"
              disabled={!form.name || !form.email || (!editing && !form.send_invite && !form.password)}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
